/* Portfolio — positions, allocation, P&L attribution and margin.
 *
 * Three forms, each chosen for the job its data does:
 *
 *   * Exposure by symbol   → a donut. Composition of a whole, few parts.
 *   * P&L by symbol        → signed bars. Magnitude WITH direction, ranked.
 *   * Margin utilisation   → a meter. One bounded value against a limit.
 *
 * The donut folds beyond six holdings into "Other" rather than generating a
 * seventh hue: a categorical palette is a fixed set, and a chart with fourteen
 * invented colours communicates nothing. The full list stays available in the
 * table beneath, which is also the relief for the light-mode steps that sit
 * below 3:1 on white.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '@/lib/api'
import { Panel } from '@/components/Panel'
import { useStore } from '@/store'
import { fmtMoney, fmtPrice, fmtQty, fmtSigned } from '@/lib/format'
import {
  Donut, DonutLegend, Empty, Meter, SignedBars,
  type BarDatum, type Slice,
} from '@/components/viz'
import type { AccountBalances } from '@/types'

const MAX_SLICES = 6

export function Portfolio() {
  const positions = useStore((s) => s.positions)
  const select = useStore((s) => s.selectInstrument)
  const [balances, setBalances] = useState<AccountBalances | null>(null)

  useEffect(() => {
    let cancelled = false
    void api.accounts()
      .then(async (a) => {
        const id = a.items[0]?.id
        if (!id) return
        const b = await api.balances(id)
        if (!cancelled) setBalances(b)
      })
      .catch(() => { /* margin panel stays blank rather than showing a guess */ })
    return () => { cancelled = true }
  }, [])

  const open = useMemo(
    () => positions.filter((p) => Number(p.quantity) !== 0), [positions])

  // --- allocation ---------------------------------------------------------
  const slices: Slice[] = useMemo(() => {
    const rows = open
      .map((p) => ({
        key: p.symbol ?? p.instrument_id,
        label: p.symbol ?? p.instrument_id.slice(0, 8),
        value: Math.abs(Number(p.exposure) || 0),
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value)

    if (rows.length <= MAX_SLICES) return rows
    // Fold the tail rather than inventing hues past the palette's last slot.
    const head = rows.slice(0, MAX_SLICES - 1)
    const tail = rows.slice(MAX_SLICES - 1)
    return [...head, {
      key: '__other__',
      label: `Other (${tail.length})`,
      value: tail.reduce((a, r) => a + r.value, 0),
    }]
  }, [open])

  // --- P&L attribution ----------------------------------------------------
  const pnlBars: BarDatum[] = useMemo(
    () => open
      .map((p) => ({
        key: p.symbol ?? p.instrument_id,
        label: p.symbol ?? p.instrument_id.slice(0, 8),
        value: Number(p.total_pnl) || 0,
      }))
      .filter((r) => r.value !== 0)
      .sort((a, b) => b.value - a.value),
    [open])

  const totalPnl = open.reduce((a, p) => a + (Number(p.total_pnl) || 0), 0)
  const realised = open.reduce((a, p) => a + (Number(p.realized_pnl) || 0), 0)
  const unrealised = open.reduce((a, p) => a + (Number(p.unrealized_pnl) || 0), 0)
  const exposure = open.reduce((a, p) => a + Math.abs(Number(p.exposure) || 0), 0)
  const marginUsed = Number(balances?.used_margin ?? NaN)
  const marginTotal = Number(balances?.total_margin ?? balances?.available_margin ?? NaN)

  if (open.length === 0) {
    return (
      <Panel title="Portfolio">
        <Empty>No open positions.</Empty>
      </Panel>
    )
  }

  return (
    <Panel title={`Portfolio · ${open.length} position${open.length === 1 ? '' : 's'}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>

        {/* Headline numbers. A hero row, not a chart — four scalars have no
            shape worth plotting. */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 'var(--sp-3)',
        }}>
          <Hero label="Total P&L" value={fmtSigned(totalPnl)}
                tone={totalPnl >= 0 ? 'profit' : 'loss'} />
          <Hero label="Realised" value={fmtSigned(realised)}
                tone={realised >= 0 ? 'profit' : 'loss'} />
          <Hero label="Unrealised" value={fmtSigned(unrealised)}
                tone={unrealised >= 0 ? 'profit' : 'loss'} />
          <Hero label="Exposure" value={fmtMoney(exposure, 'INR', true)} />
        </div>

        {Number.isFinite(marginUsed) && Number.isFinite(marginTotal) && marginTotal > 0 && (
          <Meter
            label="Margin utilisation"
            value={marginUsed}
            max={marginTotal}
            format={(v) => fmtMoney(v, 'INR', true)}
          />
        )}

        {/* Allocation */}
        <section>
          <SectionTitle>Exposure by symbol</SectionTitle>
          <div style={{
            display: 'flex', gap: 'var(--sp-4)', alignItems: 'center', flexWrap: 'wrap',
          }}>
            <Donut
              slices={slices}
              centerLabel="Exposure"
              centerValue={fmtMoney(exposure, 'INR', true)}
              formatValue={(v) => fmtMoney(v, 'INR', true)}
            />
            <div style={{ flex: 1, minWidth: 200 }}>
              <DonutLegend slices={slices} formatValue={(v) => fmtMoney(v, 'INR', true)} />
            </div>
          </div>
        </section>

        {/* P&L attribution */}
        {pnlBars.length > 0 && (
          <section>
            <SectionTitle>Profit and loss by symbol</SectionTitle>
            <SignedBars data={pnlBars} format={(v) => fmtSigned(v)} />
          </section>
        )}

        {/* The table: the full detail behind both charts, and the relief the
            light-mode palette obliges. */}
        <section>
          <SectionTitle>Positions</SectionTitle>
          <div className="scroll-x">
            <table className="qx">
              <thead>
                <tr>
                  <th>Symbol</th><th>Product</th>
                  <th className="r">Qty</th><th className="r">Avg</th>
                  <th className="r">LTP</th><th className="r">Value</th>
                  <th className="r">Realised</th><th className="r">Unrealised</th>
                  <th className="r">Total P&L</th><th className="r">Exposure</th>
                </tr>
              </thead>
              <tbody>
                {open.map((p) => {
                  const total = Number(p.total_pnl) || 0
                  return (
                    <tr key={p.id}
                        onClick={() => p.symbol && select(p.symbol)}
                        style={{ cursor: p.symbol ? 'pointer' : undefined }}>
                      <td style={{ fontWeight: 600 }}>{p.symbol ?? p.instrument_id.slice(0, 8)}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{p.product}</td>
                      <td className="num r" style={{
                        color: Number(p.quantity) >= 0 ? 'var(--buy)' : 'var(--sell)',
                      }}>
                        {fmtQty(p.quantity)}
                      </td>
                      <td className="num r">{fmtPrice(p.avg_price)}</td>
                      <td className="num r">{fmtPrice(p.last_price)}</td>
                      <td className="num r">{fmtMoney(p.market_value, p.currency, true)}</td>
                      <td className="num r">{fmtSigned(p.realized_pnl)}</td>
                      <td className="num r">{fmtSigned(p.unrealized_pnl)}</td>
                      <td className="num r" style={{
                        color: total >= 0 ? 'var(--profit)' : 'var(--loss)',
                        fontWeight: 600,
                      }}>
                        {fmtSigned(total)}
                      </td>
                      <td className="num r">{fmtMoney(p.exposure, p.currency, true)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Panel>
  )
}

function Hero({ label, value, tone }: {
  label: string; value: string; tone?: 'profit' | 'loss'
}) {
  return (
    <div style={{
      padding: 'var(--sp-2) var(--sp-3)',
      background: 'var(--bg-input)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{
        fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        {label}
      </div>
      <div className="num" style={{
        fontSize: 'var(--fs-xl)', fontWeight: 700,
        color: tone === 'profit' ? 'var(--profit)'
             : tone === 'loss' ? 'var(--loss)' : 'var(--text-primary)',
      }}>
        {value}
      </div>
    </div>
  )
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 style={{
      fontSize: 'var(--fs-2xs)', fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: 'var(--text-muted)',
      marginBottom: 'var(--sp-2)',
    }}>
      {children}
    </h3>
  )
}
